import { createContext, ReactNode, useContext, useState } from 'react';
import _ from 'lodash';
import { Button, Container, Navbar } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '../../hooks/useApiClient';
import { useQueryClient } from '@tanstack/react-query';
import type { OffertaBuilderDTO, OffertaDTO } from 'src/api';
import { toast } from 'react-toastify';
import i18next from 'i18next';

type PendingChange = {
  original: OffertaBuilderDTO;
  changes: Partial<OffertaDTO>;
};

type ContextType = {
  pendingChanges: Record<string, PendingChange>;
  setChange: (offertaId: string, field: keyof OffertaDTO, value: any, original: OffertaBuilderDTO) => void;
  discardAll: () => void;
  saveAll: () => Promise<void>;
  isSaving: boolean;
};

export const OfferMassiveUpdateContext = createContext<ContextType>({
  pendingChanges: {},
  setChange: _.noop,
  discardAll: _.noop,
  saveAll: async () => {},
  isSaving: false,
});

export const NUMERIC_FIELDS: Array<keyof OffertaDTO> = ['anticipo', 'importo_azienda', 'importo_privato', 'durata', 'km_totali'];

export const isInvalidNumeric = (field: keyof OffertaDTO, value: any): boolean => {
  if (!NUMERIC_FIELDS.includes(field)) return false;
  if (value == null || value === '') return false;
  const str = String(value).replace(',', '.').trim();
  if (str === '') return false;
  return isNaN(Number(str));
};

export const OfferMassiveUpdateProvider = ({ children }: { children: ReactNode }) => {
  const [pendingChanges, setPendingChanges] = useState<Record<string, PendingChange>>({});
  const [isSaving, setIsSaving] = useState(false);
  const { apiClient } = useApiClient();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const setChange = (offertaId: string, field: keyof OffertaDTO, value: any, original: OffertaBuilderDTO) => {
    setPendingChanges(prev => ({
      ...prev,
      [offertaId]: {
        original,
        changes: {
          ...(prev[offertaId]?.changes ?? {}),
          [field]: value,
        },
      },
    }));
  };

  const discardAll = () => setPendingChanges({});

  const saveAll = async () => {
    setIsSaving(true);

    const parseChanges = (changes: Partial<OffertaDTO>): Partial<OffertaDTO> => {
      const parsed: Partial<OffertaDTO> = {};
      for (const [key, val] of Object.entries(changes)) {
        const k = key as keyof OffertaDTO;
        if (NUMERIC_FIELDS.includes(k)) {
          const n = Number(String(val).replace(',', '.').trim());
          (parsed as any)[k] = isNaN(n) ? val : n;
        } else {
          (parsed as any)[k] = val;
        }
      }
      return parsed;
    };

    const entries = Object.entries(pendingChanges);

    const savedMap: Record<string, Partial<OffertaDTO>> = {};
    let failCount = 0;

    for (const [offertaId, { original, changes }] of entries) {
      const parsedChanges = parseChanges(changes);
      try {
        await apiClient.leasysController.putLeasys(offertaId, {
          vehicle_ids: original.vehicle_ids,
          offerta: { ...original.offerta, ...parsedChanges } as OffertaDTO,
        });
        savedMap[offertaId] = parsedChanges;
      } catch {
        failCount++;
      }
    }

    const savedIds = Object.keys(savedMap);

    if (savedIds.length > 0) {
      queryClient.setQueryData(['leasys_offers'], (old: OffertaBuilderDTO[] | undefined) => {
        if (!old) return old;
        return old.map(item => {
          const changes = savedMap[item.offerta?.id ?? ''];
          if (changes) {
            return { ...item, offerta: { ...item.offerta, ...changes } };
          }
          return item;
        });
      });

      setPendingChanges(prev => {
        const next = { ...prev };
        savedIds.forEach(id => delete next[id]);
        return next;
      });

      toast.success(i18next.t('executed_successfully'));
    }

    if (failCount > 0) {
      toast.error(i18next.t('error_massive_update'));
    }

    setIsSaving(false);
  };

  const dirtyCount = Object.keys(pendingChanges).length;

  const hasInvalidChanges = Object.values(pendingChanges).some(({ changes }) =>
    Object.entries(changes).some(([field, value]) =>
      isInvalidNumeric(field as keyof OffertaDTO, value),
    ),
  );

  return (
    <OfferMassiveUpdateContext.Provider value={{ pendingChanges, setChange, discardAll, saveAll, isSaving }}>
      {children}

      {dirtyCount > 0 && (
        <Navbar fixed="bottom" bg="dark" className="py-3 px-4">
          <Container fluid className="d-flex justify-content-between align-items-center">
            <span className="text-white">
              {t('offer_massive_update_unsaved_rows', { count: dirtyCount })}
            </span>
            <div>
              <Button
                variant="outline-light"
                className="me-2"
                onClick={discardAll}
                disabled={isSaving}
              >
                {t('discard_all_changes')}
              </Button>
              <Button
                variant="light"
                onClick={saveAll}
                disabled={isSaving || hasInvalidChanges}
              >
                {isSaving ? `${t('saving')}...` : t('save_changes')}
              </Button>
            </div>
          </Container>
        </Navbar>
      )}
    </OfferMassiveUpdateContext.Provider>
  );
};

export const useOfferMassiveUpdateContext = () => useContext(OfferMassiveUpdateContext);

export default OfferMassiveUpdateContext;
